import React, { useCallback, useMemo, useState } from 'react';
import { Select, Button, Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import {
  MUTATION_BULK_REASSIGN_REFERRER,
  QUERY_PARTNER_CONTACTS,
  QUERY_LIST_REFERRERS,
} from '~pages/dashboard/referral/partner/queries';
import classNames from 'classnames';

function PartnerDetailCommissionPlanEditor({ selectedPartners, onClose }) {
  const { t } = useTranslation();

  const [selectedReferrerId, setSelectedReferrerId] = useState();
  const [validationInfo, setValidationInfo] = useState({ selectedReferrerId: null });

  const { loading: referrersListLoading, data: referrersListResponse } = useQuery(
    QUERY_LIST_REFERRERS,
    {
      variables: {
        pageNumber: 1,
        pageSize: 100,
      },
      onError: e => {
        message.error(e?.toString());
      },
    },
  );

  const [updateReferrer, { loading: updateReferrerLoading }] = useMutation(
    MUTATION_BULK_REASSIGN_REFERRER,
    {
      refetchQueries: [QUERY_PARTNER_CONTACTS],
      onError: e => {
        message.error(e.message);
      },
    },
  );

  const handleSubmit = useCallback(async (selectedPartnerIds, selectedReferrerId) => {
    if (selectedReferrerId === undefined) {
      return setValidationInfo({
        selectedReferrerId: t('cms.referral.partner.reassign.editor.selector.error'),
      });
    }

    const response = await updateReferrer({
      variables: {
        partnerIds: selectedPartnerIds,
        referrerId: selectedReferrerId !== 'null' ? selectedReferrerId : null,
      },
    });
    if (response?.data?.bulkReassignReferrer?.result) {
      message.success(t('cms.referral.partner.reassign.editor.action.reassign.toast.success'));
      onClose();
    }
  }, []);

  const referrerList = useMemo(() => {
    const remoteReferrerList = referrersListResponse?.referrers?.edges.map(edge => edge.node) || [];
    return [{ id: null, firstName: 'None', lastName: null }].concat(remoteReferrerList);
  }, [referrersListResponse]);

  return (
    <>
      <div className="partner-reassign-editor__body">
        <section className="partner-reassign-editor__section">
          {referrersListLoading && !referrerList.length ? (
            <div className="partner-reassign-editor__spin">
              <Spin />
            </div>
          ) : (
            <main className="partner-reassign-editor__main">
              <div
                className={classNames('partner-reassign-editor__form-item', {
                  'has-error': validationInfo.selectedReferrerId,
                })}
              >
                <h3 className="partner-reassign-editor__selector-title">
                  {t('cms.referral.partner.reassign.editor.selector.title')}
                </h3>
                <Select
                  loading={referrersListLoading}
                  className={classNames('partner-reassign-editor__selector')}
                  showSearch
                  optionFilterProp={'children'}
                  value={selectedReferrerId}
                  placeholder={'Please Select Referrer'}
                  onChange={value => {
                    setSelectedReferrerId(value);
                    if (value) {
                      setValidationInfo({ selectedReferrerId: null });
                    }
                  }}
                >
                  {referrerList.map(referrer => (
                    <Select.Option key={referrer.id}>{`${referrer?.firstName || ''} ${
                      referrer?.lastName || ''
                    }`}</Select.Option>
                  ))}
                </Select>

                {validationInfo.selectedReferrerId ? (
                  <div className="ant-legacy-form-explain">
                    {t(validationInfo.selectedReferrerId)}
                  </div>
                ) : null}
              </div>
            </main>
          )}
        </section>
      </div>
      <div className="partner-reassign-editor__footer">
        <Button size="large" className="partner-reassign-editor__cancel-button" onClick={onClose}>
          {t('cms.referral.partner.detail.editor.btn.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="partner-reassign-editor__save-button"
          loading={updateReferrerLoading}
          onClick={() => handleSubmit(selectedPartners, selectedReferrerId)}
        >
          {t('cms.referral.partner.detail.editor.btn.save')}
        </Button>
      </div>
    </>
  );
}

export default PartnerDetailCommissionPlanEditor;
